import { z } from 'zod';
import { ATLAS_RUN_STATUSES } from './pipeline.js';

/** `POST /api/runs` answer. */
export const AtlasCreatedRunSchema = z.object({
  run_id: z.string(),
  started: z.boolean(),
  queued: z.boolean(),
});
export type AtlasCreatedRun = z.infer<typeof AtlasCreatedRunSchema>;

const AtlasEventSchema = z
  .object({
    type: z.string(),
    stage: z.string().nullable().optional(),
    created_at: z.string().optional(),
    detail: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .loose();

/** `GET /api/runs/{id}`: the fields the panel relies on. Unknown fields pass through. */
export const AtlasRunSchema = z
  .object({
    run_id: z.string(),
    // An unrecognised status is kept as-is rather than failing the whole read.
    status: z.enum(ATLAS_RUN_STATUSES).or(z.string()),
    status_reason: z.string().nullable().optional(),
    viewer_email: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    completed_at: z.string().nullable().optional(),
    progress: z
      .object({
        stages: z.array(
          z.object({
            n: z.string(),
            name: z.string(),
            stage: z.string().nullable(),
            kind: z.string(),
            done: z.boolean(),
          }),
        ),
        completed: z.number(),
        total: z.number(),
      })
      .optional(),
    events: z.array(AtlasEventSchema).default([]),
    exports: z.array(z.unknown()).default([]),
  })
  .loose();
export type AtlasRun = z.infer<typeof AtlasRunSchema>;

/** One row of `GET /api/runs/{id}/stages`. */
export const AtlasStageRowSchema = z
  .object({
    stage: z.string(),
    pillar_id: z.string().nullable(),
    n: z.string().nullable(),
    name: z.string(),
    status: z.enum(['pending', 'done', 'error', 'recovered']).or(z.string()),
    has_output: z.boolean(),
    completed: z.boolean(),
    written_at: z.string().nullable().optional(),
    size: z.number().nullable().optional(),
    tokens_in: z.number().default(0),
    tokens_out: z.number().default(0),
    cache_read: z.number().default(0),
    duration_ms: z.number().default(0),
    cost_usd: z.number().nullable().optional(),
    attempts: z.number().default(0),
    model_used: z.string().nullable().optional(),
    errors: z
      .array(
        z
          .object({
            type: z.string(),
            attempt: z.number().nullable().optional(),
            created_at: z.string().nullable().optional(),
            message: z.string().default(''),
          })
          .loose(),
      )
      .default([]),
  })
  .loose();
export type AtlasStageRow = z.infer<typeof AtlasStageRowSchema>;

/** The Canvas board Atlas seeded at Finalize, read from the run's `canvas` event. */
export function canvasBoardUrl(run: AtlasRun): string | null {
  for (const e of [...run.events].reverse()) {
    const url = e.detail?.board_url;
    if (e.type === 'canvas' && typeof url === 'string' && url) return url;
  }
  return null;
}

/** One row of Atlas's `GET /api/runs` list. */
export const AtlasRunListItemSchema = z
  .object({
    run_id: z.string(),
    status: z.string(),
    status_reason: z.string().nullable().optional(),
    org_name: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    completed_at: z.string().nullable().optional(),
    has_blueprint: z.boolean().nullable().optional(),
  })
  .loose();
export type AtlasRunListItem = z.infer<typeof AtlasRunListItemSchema>;

/** A run Atlas made before the control panel existed, shown read-only. */
export interface PastRun {
  atlasRunId: string;
  company: string;
  status: string;
  statusReason: string | null;
  createdAt: string | null;
  completedAt: string | null;
  hasBlueprint: boolean;
}

export interface PastRunDetail extends PastRun {
  canvasBoardUrl: string | null;
  viewerEmail: string | null;
  stagesDone: number;
  stagesTotal: number;
}
